import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ShoppingBag } from "lucide-react";

// slides: [{ src, caption }]
export const GalleryCarousel = ({ slides }) => {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const count = slides.length;

  const go = (i) => setIndex((i + count) % count);
  const next = () => go(index + 1);
  const prev = () => go(index - 1);
  const goOrder = () => navigate("/menu-samenstellen");

  useEffect(() => {
    if (paused || count <= 1) return;
    timer.current = setInterval(() => setIndex((p) => (p + 1) % count), 5000);
    return () => clearInterval(timer.current);
  }, [paused, count, index]);

  if (count === 0) return null;

  return (
    <div
      className="relative w-full overflow-hidden bg-[#0D3B4C]"
      data-testid="gallery-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative aspect-[16/9] md:aspect-[21/9]">
        {slides.map((s, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${i === index ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            data-testid={`gallery-slide-${i}`}
          >
            <button
              type="button"
              onClick={goOrder}
              aria-label="Ga naar bestellen"
              data-testid={`gallery-slide-link-${i}`}
              className="group block w-full h-full cursor-pointer"
            >
              <img src={s.src} alt={s.caption || "Authentiek Surinaams gerecht van De Zeeuwse Tori in Zeeland"} className="w-full h-full object-cover transition-transform duration-[6000ms] ease-out group-hover:scale-105" loading={i === 0 ? "eager" : "lazy"} />
              {s.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-6 md:p-10 text-left">
                  <p className="text-white font-[700] text-lg md:text-2xl tracking-tight max-w-[720px]" data-testid={`gallery-caption-${i}`}>
                    {s.caption}
                  </p>
                </div>
              )}
              <span className="absolute top-4 right-4 inline-flex items-center gap-2 bg-[#B71C1C] text-white font-[700] text-sm px-4 py-2 rounded-full shadow-lg opacity-90 group-hover:opacity-100 group-hover:-translate-y-0.5 transition-all">
                <ShoppingBag size={16} /> Bestel nu
              </span>
            </button>
          </div>
        ))}
      </div>

      {count > 1 && (
        <>
          <button
            onClick={prev}
            aria-label="Vorige foto"
            data-testid="gallery-prev"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 text-[#0D3B4C] flex items-center justify-center hover:bg-white transition-colors"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={next}
            aria-label="Volgende foto"
            data-testid="gallery-next"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 text-[#0D3B4C] flex items-center justify-center hover:bg-white transition-colors"
          >
            <ChevronRight size={24} />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2" data-testid="gallery-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => go(i)}
                aria-label={`Ga naar foto ${i + 1}`}
                data-testid={`gallery-dot-${i}`}
                className={`h-2.5 rounded-full transition-all ${i === index ? "w-7 bg-[#F4D03F]" : "w-2.5 bg-white/60 hover:bg-white"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
